/**
 * Projective 3×3 matrix (homography) for UI `transform`. Started as a 2×3 affine (UI-17); UI-17c
 * generalized it to a homography so `perspective()` + `rotateX/Y` work: a flat z=0 box under a 4×4
 * (perspective) transform projects to a 2D homography, which is all a paint-only, layout-flat UI needs.
 *
 *   [a c e]                 (a·x + c·y + e)       (b·x + d·y + f)
 *   [b d f]   maps (x,y) → ( ───────────── ,     ───────────── )
 *   [g h 1]                 (g·x + h·y + 1)       (g·x + h·y + 1)
 *
 * Affine is the g=h=0 fast path (w≡1, no divide) — UIBatch keeps axis-aligned/affine quads in one
 * draw; perspective quads (g≠0 ∥ h≠0) are drawn as their projected screen quad and the fragment
 * recovers `local` analytically via the INVERSE homography (perspective-correct, no vertex w).
 *
 * `fromTransform` bakes a decl into this in PHYSICAL px (paint at `scale`) or LOGICAL px (hit-test at
 * scale 1, then inverted to map the pointer back). Transforms are PAINT-ONLY — layout stays 2D flexbox.
 */
import type { TransformDecl, TransformOrigin } from '../types';

export interface Mat3 {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  /** projective row [g h 1]; both 0 ⇒ affine (no perspective). */
  g: number;
  h: number;
}
/** @deprecated name kept for call sites — it is a full homography now, not just 2×3 affine. */
export type Mat2D = Mat3;

export const IDENTITY: Mat3 = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0, g: 0, h: 0 };

export function isIdentity(m: Mat3): boolean {
  return m.a === 1 && m.b === 0 && m.c === 0 && m.d === 1 && m.e === 0 && m.f === 0 && m.g === 0 && m.h === 0;
}

/** True when the matrix is a plain affine (no perspective) — UIBatch's fast, batchable path. */
export function isAffine(m: Mat3): boolean {
  return m.g === 0 && m.h === 0;
}

/** m∘n — the matrix that applies n THEN m (CSS `transform: m n` = this order). Normalized to [g h 1]. */
export function multiply(m: Mat3, n: Mat3): Mat3 {
  // rows of m × cols of n, with m/n bottom rows [m.g m.h 1] / [n.g n.h 1]
  const a = m.a * n.a + m.c * n.b + m.e * n.g;
  const b = m.b * n.a + m.d * n.b + m.f * n.g;
  const g = m.g * n.a + m.h * n.b + n.g;
  const c = m.a * n.c + m.c * n.d + m.e * n.h;
  const d = m.b * n.c + m.d * n.d + m.f * n.h;
  const h = m.g * n.c + m.h * n.d + n.h;
  const e = m.a * n.e + m.c * n.f + m.e;
  const f = m.b * n.e + m.d * n.f + m.f;
  const w = m.g * n.e + m.h * n.f + 1;
  const s = Math.abs(w) < 1e-12 ? 1 : 1 / w; // renormalize bottom-right → 1
  return { a: a * s, b: b * s, c: c * s, d: d * s, e: e * s, f: f * s, g: g * s, h: h * s };
}

/** Transform a point (with perspective divide); writes into `out` to avoid per-corner allocation. */
export function apply(m: Mat3, x: number, y: number, out: [number, number]): [number, number] {
  const w = m.g * x + m.h * y + 1;
  const iw = Math.abs(w) < 1e-9 ? 0 : 1 / w;
  out[0] = (m.a * x + m.c * y + m.e) * iw;
  out[1] = (m.b * x + m.d * y + m.f) * iw;
  return out;
}

/** Inverse homography, or null when singular (degenerate scale / edge-on tilt → node unhittable). */
export function invert(m: Mat3): Mat3 | null {
  // full 3×3 with bottom row [g h 1]
  const A = m.a, B = m.c, C = m.e, D = m.b, E = m.d, F = m.f, G = m.g, H = m.h;
  const det = A * (E - F * H) - B * (D - F * G) + C * (D * H - E * G);
  if (Math.abs(det) < 1e-12) return null;
  const id = 1 / det;
  // adjugate (cofactors, transposed) — inverse of [[A B C],[D E F],[G H 1]]
  const i00 = (E - F * H) * id;
  const i01 = (C * H - B) * id;
  const i02 = (B * F - C * E) * id;
  const i10 = (F * G - D) * id;
  const i11 = (A - C * G) * id;
  const i12 = (C * D - A * F) * id;
  const i20 = (D * H - E * G) * id;
  const i21 = (B * G - A * H) * id;
  const i22 = (A * E - B * D) * id;
  const s = Math.abs(i22) < 1e-12 ? 1 : 1 / i22;
  return {
    a: i00 * s, c: i01 * s, e: i02 * s,
    b: i10 * s, d: i11 * s, f: i12 * s,
    g: i20 * s, h: i21 * s,
  };
}

// ---------------------------------------------------------------------------
// 4×4 helpers (column-vector, row-major m[r*4+c]) — only used to bake the decl, then collapsed to 3×3

type M4 = number[];
const m4ident = (): M4 => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

function m4mul(a: M4, b: M4): M4 {
  const o = new Array<number>(16);
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      o[r * 4 + c] = a[r * 4] * b[c] + a[r * 4 + 1] * b[4 + c] + a[r * 4 + 2] * b[8 + c] + a[r * 4 + 3] * b[12 + c];
    }
  }
  return o;
}

function m4translate(tx: number, ty: number, tz: number): M4 {
  const m = m4ident();
  m[3] = tx;
  m[7] = ty;
  m[11] = tz;
  return m;
}

/** Bake a `transform` decl into a homography for a box at (x,y) size (w,h) in the caller's space
 *  (physical px at `scale`, or logical px at scale 1). px translate/translateZ/perspective distances
 *  scale; %-translate + origin are box fractions. Functions apply left-to-right (CSS), origin-pivoted. */
export function fromTransform(
  decl: TransformDecl,
  origin: TransformOrigin,
  x: number,
  y: number,
  w: number,
  h: number,
  scale: number,
): Mat3 {
  const ox = x + origin.x * w;
  const oy = y + origin.y * h;
  let m = m4ident();
  for (const fn of decl) {
    let step: M4;
    switch (fn.fn) {
      case 'translate':
        step = m4translate(fn.x * scale + fn.xPct * w, fn.y * scale + fn.yPct * h, 0);
        break;
      case 'translateZ':
        step = m4translate(0, 0, fn.z * scale);
        break;
      case 'scale':
        step = m4ident();
        step[0] = fn.x;
        step[5] = fn.y;
        break;
      case 'rotate': {
        const cos = Math.cos(fn.rad), sin = Math.sin(fn.rad);
        step = m4ident();
        step[0] = cos; step[1] = -sin; step[4] = sin; step[5] = cos;
        break;
      }
      case 'rotateX': {
        const cos = Math.cos(fn.rad), sin = Math.sin(fn.rad);
        step = m4ident();
        step[5] = cos; step[6] = -sin; step[9] = sin; step[10] = cos;
        break;
      }
      case 'rotateY': {
        const cos = Math.cos(fn.rad), sin = Math.sin(fn.rad);
        step = m4ident();
        step[0] = cos; step[2] = sin; step[8] = -sin; step[10] = cos;
        break;
      }
      case 'skew':
        step = m4ident();
        step[1] = Math.tan(fn.x);
        step[4] = Math.tan(fn.y);
        break;
      case 'perspective':
        step = m4ident();
        step[14] = -1 / Math.max(1e-3, fn.d * scale); // w' = 1 - z/d
        break;
    }
    m = m4mul(m, step);
  }
  // origin sandwich (in the z=0 plane), then collapse the flat box to a 2D homography
  m = m4mul(m4translate(ox, oy, 0), m4mul(m, m4translate(-ox, -oy, 0)));
  const wc = m[15];
  const s = Math.abs(wc) < 1e-9 ? 1 : 1 / wc;
  return {
    a: m[0] * s, c: m[1] * s, e: m[3] * s,
    b: m[4] * s, d: m[5] * s, f: m[7] * s,
    g: m[12] * s, h: m[13] * s,
  };
}
